import { Capacitor } from '@capacitor/core';
import {
  addDays,
  areIntervalsOverlapping,
  format,
  isToday,
  parseISO,
  startOfWeek,
} from 'date-fns';
import { useEffect, useRef, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { getCompactWeekGrid } from '@/components/calendar/compact-week-grid';
import { AddEventDialog } from '@/components/calendar/components/dialogs/add-event-dialog';
import { EventDetailsDialog } from '@/components/calendar/components/dialogs/event-details-dialog';
import { DroppableTimeBlock } from '@/components/calendar/components/dnd/droppable-time-block';
import { CalendarTimeline } from '@/components/calendar/components/week-and-day-view/calendar-time-line';
import { DayViewMultiDayEventsRow } from '@/components/calendar/components/week-and-day-view/day-view-multi-day-events-row';
import { EventBlock } from '@/components/calendar/components/week-and-day-view/event-block';
import { eventBlockBodyVariants } from '@/components/calendar/components/week-and-day-view/event-block-body';
import { HoverCreateSlot } from '@/components/calendar/components/week-and-day-view/hover-create-slot';
import {
  HOUR_LABEL_OVERHANG_PX,
  HOUR_PX,
} from '@/components/calendar/constants';
import { useCalendar } from '@/components/calendar/contexts/calendar-context';
import {
  getCalendarEventColor,
  pastelEventColors,
} from '@/components/calendar/event-colors';
import { useGoToDayView } from '@/components/calendar/hooks/use-go-to-day-view';

import {
  findResolvedShift,
  getEventBlockStyle,
  getVisibleHours,
  groupEvents,
  isHourDisabledForColumn,
  isTimeBlockEvent,
} from '@/components/calendar/helpers';
import { useColumnMatcher } from '@/components/calendar/hooks/use-column-matcher';
import { useIsMobile } from '@/hooks/use-mobile';
import { zonedDateString, zonedEvent } from '@/lib/timezone';
import { cn } from '@/lib/utils';

import type { IEvent, IUser } from '@/components/calendar/interfaces';
import type { CSSProperties } from 'react';

interface IProps {
  singleDayEvents: IEvent[];
  multiDayEvents: IEvent[];
  /** Number of day-columns to render (3 = "3 day", 7 = week). */
  dayCount: number;
}

const RAIL_W = 56; // px — hours rail width
const AVATAR_W = 96; // px — far-left person column width
// A full week on a phone: seven day columns have to share ~330px, so the person
// column shrinks to an avatar and the hours rail to its numerals.
const COMPACT_AVATAR_W = 48;
const COMPACT_RAIL_W = 40;
const LIST_ROW_MIN = 160; // px — min height of a person row in list mode
const LIST_ROW_COMPACT_MIN = 96; // px — the same, on a phone's seven-day week
const DAY_HEADER_PX = 56; // px — day header row height (sticky offset for avatar)

type DialogComponentType = NonNullable<
  ReturnType<typeof useCalendar>['config']['customAddDialog']
>;

/**
 * Resource × days grid used by the "3 day" / multi-day view.
 *
 * - One person selected → time grid: a far-left avatar column, a times column,
 *   then one time-grid column per day.
 * - Multiple people → no time column; people are rows, days are columns, and
 *   each person×day cell lists that person's bookings stacked as a list.
 *
 * Days are always the columns and people the rows.
 */
export function CalendarResourceDaysView({
  singleDayEvents,
  multiDayEvents,
  dayCount,
}: IProps) {
  const {
    selectedDate,
    selectedUserIds,
    users,
    visibleHours,
    workingHours,
    resolvedShifts,
    config,
    setEditOpeningHoursDate,
    timeZone,
  } = useCalendar();

  const DialogComponent = config.customAddDialog ?? AddEventDialog;

  const { hours, earliestEventHour, latestEventHour, firstEventHour } =
    getVisibleHours(visibleHours, singleDayEvents, timeZone);
  const firstHour = hours[0] ?? earliestEventHour;
  const lastHour = (hours[hours.length - 1] ?? latestEventHour) + 1;

  const rangeStart = dayCount >= 7 ? startOfWeek(selectedDate) : selectedDate;
  const days = Array.from({ length: dayCount }, (_, i) =>
    addDays(rangeStart, i)
  );
  const isMobile = useIsMobile();
  // On a phone a week must show all seven days at once, so the columns share
  // the width instead of holding a 140px minimum and scrolling.
  const compact = (isMobile || Capacitor.isNativePlatform()) && dayCount >= 7;
  const gridStyle: CSSProperties = {
    gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))`,
    // Keep week columns readable when the appointment side panel narrows the
    // calendar. The shared scroll viewport then exposes the later days rather
    // than crushing seven labels and booking chips into the remaining width.
    ...(dayCount >= 7 && !compact ? { minWidth: dayCount * 140 } : {}),
  };
  const hasToday = days.some((d) => isToday(d));

  const visibleStaff =
    selectedUserIds === 'all'
      ? users
      : users.filter((u) => selectedUserIds.includes(u.id));

  const isTimeline = visibleStaff.length <= 1;

  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport || visibleStaff.length === 0) return;
    const measure = () => setViewportHeight(viewport.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [visibleStaff.length]);
  const compactGrid = getCompactWeekGrid({
    days,
    staffId: visibleStaff[0]?.id,
    workingHours,
    resolvedShifts,
    events: singleDayEvents,
    timeZone,
    viewportHeight,
  });
  const hourPx = compact ? compactGrid.hourPx : HOUR_PX;
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport || !isTimeline) return;
    // Never scroll PAST the day's first booking. Defaulting straight to 09:00
    // parked an 08:30 appointment above the fold and bisected it against the
    // sticky header — the bottom few pixels of its text smeared along the edge.
    const targetHour = compact
      ? compactGrid.firstHour
      : firstEventHour === undefined
        ? 9
        : Math.min(9, firstEventHour);
    const offsetHours = Math.max(0, targetHour - earliestEventHour);
    // Clear the hour label's overhang so the topmost one isn't bisected by the
    // sticky header. See HOUR_LABEL_OVERHANG_PX.
    viewport.scrollTop = Math.max(
      0,
      offsetHours * hourPx - HOUR_LABEL_OVERHANG_PX
    );
  }, [
    earliestEventHour,
    firstEventHour,
    isTimeline,
    compact,
    compactGrid.firstHour,
    hourPx,
  ]);

  if (visibleStaff.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No team members selected
      </div>
    );
  }

  const avatarW = compact ? COMPACT_AVATAR_W : AVATAR_W;
  const railW = compact ? COMPACT_RAIL_W : RAIL_W;
  const cornerWidth = isTimeline ? avatarW + railW : avatarW;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <DayViewMultiDayEventsRow
        selectedDate={selectedDate}
        multiDayEvents={multiDayEvents}
      />

      <div
        role="region"
        ref={scrollViewportRef}
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto"
        aria-label="Calendar days"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users need to scroll this region.
        tabIndex={0}
      >
        {/* Day header row — pinned to the top. */}
        <div className="sticky top-0 z-30 flex shrink-0 border-b bg-background">
          <div
            className="sticky left-0 z-40 shrink-0 bg-background"
            style={{ width: cornerWidth }}
          />
          <div className="grid flex-1 divide-x border-l" style={gridStyle}>
            {days.map((day) => (
              <DayHeader
                key={format(day, 'yyyy-MM-dd')}
                day={day}
                compact={compact}
              />
            ))}
          </div>
        </div>

        {isTimeline ? (
          <TimelineBand
            staff={visibleStaff[0]}
            days={days}
            hours={hours}
            gridStyle={gridStyle}
            firstHour={firstHour}
            lastHour={lastHour}
            hasToday={hasToday}
            compact={compact}
            hourPx={hourPx}
            singleDayEvents={singleDayEvents}
            earliestEventHour={earliestEventHour}
            latestEventHour={latestEventHour}
            workingHours={workingHours}
            resolvedShifts={resolvedShifts}
            DialogComponent={DialogComponent}
            onEditOpeningHours={setEditOpeningHoursDate}
          />
        ) : (
          visibleStaff.map((staff) => (
            <ListRow
              key={staff.id}
              staff={staff}
              days={days}
              gridStyle={gridStyle}
              compact={compact}
              singleDayEvents={singleDayEvents}
              DialogComponent={DialogComponent}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DayHeader({ day, compact }: { day: Date; compact: boolean }) {
  const today = isToday(day);
  const goToDay = useGoToDayView();

  if (compact) {
    // Weekday initial over the date: the whole seven-day header fits a phone.
    return (
      <button
        type="button"
        onClick={() => goToDay(day)}
        aria-label={`View ${format(day, 'EEEE, MMMM d')}`}
        className="flex w-full min-w-0 cursor-pointer flex-col items-center justify-center gap-0.5 overflow-hidden focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
        style={{ height: DAY_HEADER_PX }}
      >
        <span
          className={cn(
            'text-[10px] font-medium uppercase',
            today ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {format(day, 'EEEEE')}
        </span>
        <span
          className={cn(
            'flex size-6 items-center justify-center rounded-full text-xs font-semibold',
            today ? 'bg-primary text-primary-foreground' : 'text-foreground'
          )}
        >
          {format(day, 'd')}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => goToDay(day)}
      aria-label={`View ${format(day, 'EEEE, MMMM d')}`}
      className="flex w-full min-w-0 cursor-pointer items-center gap-1 overflow-hidden px-1.5 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
      style={{ height: DAY_HEADER_PX }}
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
          today ? 'bg-primary text-primary-foreground' : 'text-foreground'
        )}
      >
        {format(day, 'd')}
      </span>
      <span
        className={cn(
          'min-w-0 truncate text-sm font-medium',
          today ? 'text-primary' : 'text-muted-foreground'
        )}
      >
        {format(day, 'EEEE')}
      </span>
    </button>
  );
}

function PersonAvatar({ staff, compact }: { staff: IUser; compact: boolean }) {
  const { config } = useCalendar();
  const Menu = config.staffHeaderMenu;
  const initials = staff.name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const trigger = (
    <button
      type="button"
      aria-label={staff.name}
      title={staff.name}
      className="flex flex-col items-center gap-1 rounded-md p-1 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
    >
      <Avatar className={compact ? 'size-8' : 'size-10'}>
        {staff.picturePath && (
          <AvatarImage src={staff.picturePath} alt={staff.name} />
        )}
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>
      {compact ? null : (
        <span className="line-clamp-2 text-center text-xs font-medium leading-tight">
          {staff.name}
        </span>
      )}
    </button>
  );

  if (!Menu) return trigger;
  return <Menu staff={staff}>{trigger}</Menu>;
}

// ================= Timeline mode (single person) ================= //

interface TimelineBandProps {
  staff: IUser;
  days: Date[];
  hours: number[];
  gridStyle: CSSProperties;
  firstHour: number;
  lastHour: number;
  hasToday: boolean;
  compact: boolean;
  hourPx: number;
  singleDayEvents: IEvent[];
  earliestEventHour: number;
  latestEventHour: number;
  workingHours: ReturnType<typeof useCalendar>['workingHours'];
  resolvedShifts: ReturnType<typeof useCalendar>['resolvedShifts'];
  DialogComponent: DialogComponentType;
  onEditOpeningHours: (date: Date) => void;
}

function TimelineBand({
  staff,
  days,
  hours,
  gridStyle,
  firstHour,
  lastHour,
  hasToday,
  compact,
  hourPx,
  singleDayEvents,
  earliestEventHour,
  latestEventHour,
  workingHours,
  resolvedShifts,
  DialogComponent,
  onEditOpeningHours,
}: TimelineBandProps) {
  const avatarW = compact ? COMPACT_AVATAR_W : AVATAR_W;
  const railW = compact ? COMPACT_RAIL_W : RAIL_W;
  return (
    <div className="flex shrink-0">
      {/* Far-left avatar column — avatar pinned to the top so it stays
          visible while the day grids scroll vertically. */}
      <div
        className="sticky left-0 z-30 shrink-0 border-r bg-background"
        style={{ width: avatarW }}
      >
        <div
          className="sticky flex flex-col items-center gap-1 p-2"
          style={{ top: DAY_HEADER_PX }}
        >
          <PersonAvatar staff={staff} compact={compact} />
        </div>
      </div>

      {/* Times column */}
      <div
        className="sticky z-20 shrink-0 border-r bg-background"
        style={{ width: railW, left: avatarW }}
      >
        <div className="relative">
          {hours.map((hour, index) => (
            <div
              key={hour}
              className="relative"
              style={{ height: `${hourPx}px` }}
            >
              <div
                className={cn(
                  'absolute -top-2.5 flex h-5 items-center',
                  compact ? 'right-1' : 'right-2'
                )}
              >
                {index !== 0 && (
                  <span
                    className={cn(
                      'font-medium text-muted-foreground',
                      compact ? 'text-[10px]' : 'text-xs'
                    )}
                  >
                    {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
                  </span>
                )}
              </div>
            </div>
          ))}
          {hasToday && (
            <CalendarTimeline
              variant="rail"
              firstVisibleHour={firstHour}
              lastVisibleHour={lastHour}
            />
          )}
        </div>
      </div>

      {/* One time-grid column per day */}
      <div className="grid flex-1 divide-x border-l" style={gridStyle}>
        {days.map((day) => (
          <TimelineDayColumn
            key={format(day, 'yyyy-MM-dd')}
            staff={staff}
            day={day}
            hourPx={hourPx}
            compact={compact}
            hours={hours}
            firstHour={firstHour}
            lastHour={lastHour}
            singleDayEvents={singleDayEvents}
            earliestEventHour={earliestEventHour}
            latestEventHour={latestEventHour}
            workingHours={workingHours}
            resolvedShifts={resolvedShifts}
            DialogComponent={DialogComponent}
            onEditOpeningHours={onEditOpeningHours}
          />
        ))}
      </div>
    </div>
  );
}

interface TimelineDayColumnProps {
  hourPx: number;
  compact: boolean;
  staff: IUser;
  day: Date;
  hours: number[];
  firstHour: number;
  lastHour: number;
  singleDayEvents: IEvent[];
  earliestEventHour: number;
  latestEventHour: number;
  workingHours: ReturnType<typeof useCalendar>['workingHours'];
  resolvedShifts: ReturnType<typeof useCalendar>['resolvedShifts'];
  DialogComponent: DialogComponentType;
  onEditOpeningHours: (date: Date) => void;
}

function TimelineDayColumn({
  hourPx,
  compact,
  staff,
  day,
  hours,
  firstHour,
  lastHour,
  singleDayEvents,
  earliestEventHour,
  latestEventHour,
  workingHours,
  resolvedShifts,
  DialogComponent,
  onEditOpeningHours,
}: TimelineDayColumnProps) {
  const matchColumn = useColumnMatcher();
  const { timeZone } = useCalendar();
  const dayEvents = singleDayEvents.filter(
    (event) =>
      matchColumn(event, staff) &&
      (zonedDateString(parseISO(event.startDate), timeZone) ===
        format(day, 'yyyy-MM-dd') ||
        zonedDateString(parseISO(event.endDate), timeZone) ===
          format(day, 'yyyy-MM-dd'))
  );
  const groupedEvents = groupEvents(dayEvents);
  const dayShift = findResolvedShift(resolvedShifts, staff.id, day);

  return (
    <div className="relative">
      {hours.map((hour, index) => {
        const isDisabled = isHourDisabledForColumn(
          day,
          hour,
          workingHours,
          dayShift,
          { practitionerScoped: true }
        );
        return (
          <div
            key={hour}
            className={cn(
              'relative',
              isDisabled && 'bg-calendar-disabled-hour'
            )}
            style={{ height: `${hourPx}px` }}
          >
            {index !== 0 && (
              <div className="pointer-events-none absolute inset-x-0 top-0 border-b" />
            )}
            {isDisabled ? (
              <button
                type="button"
                aria-label="Edit opening hours"
                className="absolute inset-0 cursor-pointer transition-colors hover:bg-muted/40"
                onClick={() => onEditOpeningHours(day)}
              />
            ) : (
              [0, 15, 30, 45].map((minute, slotIndex) => (
                <DroppableTimeBlock
                  key={minute}
                  date={day}
                  hour={hour}
                  minute={minute}
                  hourPx={hourPx}
                >
                  <DialogComponent
                    startDate={day}
                    startTime={{ hour, minute }}
                    practitionerId={staff.id}
                  >
                    <HoverCreateSlot
                      top={slotIndex * (hourPx / 4)}
                      height={hourPx / 4}
                      hour={hour}
                      minute={minute}
                    />
                  </DialogComponent>
                </DroppableTimeBlock>
              ))
            )}
          </div>
        );
      })}

      {groupedEvents.map((group, groupIndex) =>
        group.map((event) => {
          let style = getEventBlockStyle(
            event,
            day,
            groupIndex,
            groupedEvents.length,
            { from: earliestEventHour, to: latestEventHour },
            timeZone
          );
          const hasOverlap = groupedEvents.some(
            (otherGroup, otherIndex) =>
              otherIndex !== groupIndex &&
              otherGroup.some((otherEvent) =>
                areIntervalsOverlapping(
                  {
                    start: parseISO(event.startDate),
                    end: parseISO(event.endDate),
                  },
                  {
                    start: parseISO(otherEvent.startDate),
                    end: parseISO(otherEvent.endDate),
                  }
                )
              )
          );
          if (!hasOverlap) style = { ...style, width: '100%', left: '0%' };

          return (
            <div
              key={`${event.id}-${staff.id}`}
              className={cn('absolute', compact ? 'p-px' : 'p-1')}
              style={style}
              data-event-block
            >
              <EventBlock event={event} hourPx={hourPx} compact={compact} />
            </div>
          );
        })
      )}

      {isToday(day) && (
        <CalendarTimeline
          variant="column"
          firstVisibleHour={firstHour}
          lastVisibleHour={lastHour}
        />
      )}
    </div>
  );
}

// ================= List mode (multiple people) ================= //

interface ListRowProps {
  staff: IUser;
  days: Date[];
  gridStyle: CSSProperties;
  compact: boolean;
  singleDayEvents: IEvent[];
  DialogComponent: DialogComponentType;
}

function ListRow({
  staff,
  days,
  gridStyle,
  compact,
  singleDayEvents,
  DialogComponent,
}: ListRowProps) {
  return (
    <div
      className="flex flex-1 border-b"
      style={{ minHeight: compact ? LIST_ROW_COMPACT_MIN : LIST_ROW_MIN }}
    >
      <div
        className={cn(
          'sticky left-0 z-30 flex shrink-0 flex-col items-center justify-center gap-1 border-r bg-background',
          compact ? 'p-1' : 'p-2'
        )}
        style={{ width: compact ? COMPACT_AVATAR_W : AVATAR_W }}
      >
        <PersonAvatar staff={staff} compact={compact} />
      </div>

      <div className="grid flex-1 divide-x border-l" style={gridStyle}>
        {days.map((day) => (
          <ListCell
            key={format(day, 'yyyy-MM-dd')}
            staff={staff}
            day={day}
            compact={compact}
            singleDayEvents={singleDayEvents}
            DialogComponent={DialogComponent}
          />
        ))}
      </div>
    </div>
  );
}

interface ListCellProps {
  staff: IUser;
  day: Date;
  compact: boolean;
  singleDayEvents: IEvent[];
  DialogComponent: DialogComponentType;
}

function ListCell({
  staff,
  day,
  compact,
  singleDayEvents,
  DialogComponent,
}: ListCellProps) {
  const matchColumn = useColumnMatcher();
  const { timeZone, colorBy, config } = useCalendar();
  const dayEvents = singleDayEvents
    .filter(
      (event) =>
        matchColumn(event, staff) &&
        zonedDateString(parseISO(event.startDate), timeZone) ===
          format(day, 'yyyy-MM-dd')
    )
    .sort(
      (a, b) =>
        parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
    );

  return (
    <div className="relative h-full">
      {/* Background is an add-booking trigger; chips sit on top. */}
      <DialogComponent startDate={day} practitionerId={staff.id}>
        <button
          type="button"
          aria-label="Add booking"
          className="absolute inset-0 cursor-pointer transition-colors hover:bg-muted/30"
        />
      </DialogComponent>

      <div
        className={cn(
          'pointer-events-none relative flex flex-col',
          compact ? 'gap-0.5 p-0.5' : 'gap-1 p-1.5'
        )}
      >
        {dayEvents.map((event) => (
          <div key={`${event.id}-${staff.id}`} className="pointer-events-auto">
            <EventDetailsDialog event={event}>
              <button
                type="button"
                aria-label={`${format(zonedEvent(event.startDate, timeZone), 'HH:mm')} ${event.title}`}
                className={cn(
                  eventBlockBodyVariants({
                    color: config.colorByEnabled
                      ? getCalendarEventColor(event, colorBy)
                      : event.color,
                  }),
                  'w-full min-w-0 gap-0.5 overflow-hidden text-left',
                  // A seven-day phone week gives a chip ~45px: just the start
                  // time, tap for the rest.
                  compact && 'px-0.5 py-0.5',
                  config.colorByEnabled &&
                    !isTimeBlockEvent(event) &&
                    pastelEventColors[getCalendarEventColor(event, colorBy)]
                )}
              >
                {compact ? (
                  <span className="truncate text-[9px] leading-tight font-semibold">
                    {typeof event.metadata?.leadName === 'string'
                      ? event.metadata.leadName
                      : format(zonedEvent(event.startDate, timeZone), 'HH:mm')}
                  </span>
                ) : (
                  <>
                    <span className="truncate text-[11px] font-semibold">
                      {typeof event.metadata?.leadName === 'string' && (
                        <span className="block truncate">
                          {event.metadata.leadName}
                        </span>
                      )}
                      {format(zonedEvent(event.startDate, timeZone), 'HH:mm')}–
                      {format(zonedEvent(event.endDate, timeZone), 'HH:mm')}
                    </span>
                    <span className="truncate text-[11px]">
                      {typeof event.metadata?.serviceName === 'string'
                        ? event.metadata.serviceName
                        : event.title}
                    </span>
                  </>
                )}
              </button>
            </EventDetailsDialog>
          </div>
        ))}
      </div>
    </div>
  );
}
